/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable no-unused-vars, @typescript-eslint/no-unused-vars */
import { Box, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import Button from '@Components/Button';
import { AutocompleteOptions, SelectFieldWrapper } from '@Components/index';
import { GlobalSettingInputs } from '@Containers/Settings/AdminSettings/GlobalSettings/GlobalSettings.types';
import ProviderDialInNumberModal from '@Containers/Settings/AdminSettings/ProviderDialInNumberModal';
import { GlobalSettingName, VoiceProvider } from '@Models/callerIds.models';
import {
  useGetGlobalSettingsQuery,
  useUpdateGlobalSettingsMutation,
} from '@Services/settings.service';
import { BaseAlertProps } from '@Typings/global.types';
import useAlert from '@Utils/hooks/useAlert.hook';

const GlobalSettings: React.FunctionComponent = (): JSX.Element => {
  const { t } = useTranslation();
  const { triggerAlert } = useAlert();

  const [modalIsOpened, setModalIsOpened] = useState<boolean>(false);
  const [selectedVoiceProvider, setSelectedVoiceProvider] =
    useState<VoiceProvider | null>(null);
  const [, setAlertState] = useState<BaseAlertProps>({
    openAlert: false,
  });

  /** RTK QUERY HOOK */
  // load a list of global settings
  const { data: dataOfGlobalSettings = [] } = useGetGlobalSettingsQuery();

  // update global settings
  const [updateGlobalSettings, updateGlobalSettingsMethods] =
    useUpdateGlobalSettingsMutation();

  /**
   * Error alerts for updating global alerts
   */
  useEffect(() => {
    if (updateGlobalSettingsMethods.error) {
      triggerAlert(updateGlobalSettingsMethods.error, setAlertState);
    }
  }, [updateGlobalSettingsMethods, triggerAlert]);

  const handleDialInDropdownChange = (
    selectedItem: AutocompleteOptions,
    provider: VoiceProvider
  ) =>
    updateGlobalSettings({
      id: selectedItem.value,
      settingName: GlobalSettingName.VOICE_PROVIDER_DIAL_IN_NUMBER,
      voiceProvider: provider,
      settingValue: selectedItem.label,
    });

  const methods = useForm<GlobalSettingInputs>({
    defaultValues: {},
    mode: 'onChange',
    shouldUnregister: true,
  });

  // Utility function to get options for a specific provider
  const getProviderOptions = (provider: VoiceProvider): AutocompleteOptions[] =>
    dataOfGlobalSettings
      .filter((e) => e.voiceProvider === provider)
      .map((e) => ({
        label: e.settingValue,
        value: e.id,
      }));

  // Set default values for dropdowns
  useEffect(() => {
    if (dataOfGlobalSettings.length > 0) {
      const defaultValues = Object.values(VoiceProvider).reduce(
        (acc, provider) => {
          const enabledOption = dataOfGlobalSettings.find(
            (e) => e.voiceProvider === provider && e.isEnabled
          );
          if (enabledOption) {
            acc[`${provider.toLowerCase()}Dropdown`] = {
              label: enabledOption.settingValue,
              value: enabledOption.id,
            };
          }
          return acc;
        },
        {} as Record<string, { label: string; value: string }>
      );

      methods.reset(defaultValues);
    }
  }, [methods, dataOfGlobalSettings]);

  const openDialInNumberModal = (voiceProvider: VoiceProvider) => {
    setModalIsOpened(true);
    setSelectedVoiceProvider(voiceProvider);
  };

  const renderProviderSection = (
    provider: VoiceProvider,
    options: AutocompleteOptions[]
  ) => (
    <Box>
      <Typography variant="input" fontWeight="bold" sx={{ mt: 2 }}>
        {t(
          `reports.${
            provider.charAt(0).toUpperCase() + provider.slice(1).toLowerCase()
          }`
        )}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: '4rem',
        }}
      >
        <SelectFieldWrapper
          name={`${provider.toLowerCase()}Dropdown`}
          options={options}
          multiple={false}
          onChange={(_, selectedItem) =>
            handleDialInDropdownChange(selectedItem, provider)
          }
          sx={{ minWidth: '20rem', maxWidth: '20rem' }}
          disableClearable
        />
        <Button
          color="primary"
          icon="add"
          text={t('common.messages.add')}
          onClick={() => openDialInNumberModal(provider)}
        />
      </Box>
    </Box>
  );

  return (
    <>
      <FormProvider {...methods}>
        <Box>
          <Typography
            variant="input"
            fontWeight="bold"
            fontSize="18px"
            sx={{ mt: 3 }}
          >
            {t('adminSettings.Dial In Numbers Configuration')}
          </Typography>
          <Box>
            {renderProviderSection(
              VoiceProvider.TWILIO,
              getProviderOptions(VoiceProvider.TWILIO)
            )}
            {renderProviderSection(
              VoiceProvider.VONAGE,
              getProviderOptions(VoiceProvider.VONAGE)
            )}
            {renderProviderSection(
              VoiceProvider.TELNYX,
              getProviderOptions(VoiceProvider.TELNYX)
            )}
          </Box>
        </Box>
      </FormProvider>
      <ProviderDialInNumberModal
        isOpen={modalIsOpened}
        setIsOpen={setModalIsOpened}
        availableLimitToBuy={1}
        voiceProvider={selectedVoiceProvider || VoiceProvider.AUTO}
      />
    </>
  );
};

export default GlobalSettings;
